import { z } from 'zod';
import { FilterDefinitionForExtras } from 'libs/end-to-end/src/types/databases/filters/filter-definition';
import { ControlVariableDefinition } from 'libs/end-to-end/src/types/databases/controls/control-variable';
import { ExplanationsOutputDictionary } from 'libs/end-to-end/src/explanations';

/**
 * @deprecated
 */
export const SuccessPhenotypeAttribute = z
  .object({
    name: z.string(),
    value: z.string(),
  })
  .describe('SuccessPhenotypeAttribute');

/**
 * @deprecated
 */
export type SuccessPhenotypeAttribute = z.infer<
  typeof SuccessPhenotypeAttribute
>;

/**
 * @deprecated
 */
export const SuccessPhenotypeCompetencyFlag = z
  .enum(['MostImportant'])
  .describe('SuccessPhenotypeCompetencyFlag');

/**
 * @deprecated
 */
export const SuccessPhenotypeCompetency = z
  .object({
    name: z.string(),
    rating: z.number().min(-1).max(1),
    flag: SuccessPhenotypeCompetencyFlag.nullable(),
  })
  .describe('SuccessPhenotypeCompetency');

/**
 * @deprecated
 */
export type SuccessPhenotypeCompetency = z.infer<
  typeof SuccessPhenotypeCompetency
>;

/**
 * @deprecated
 */
export const SuccessPhenotypeResult = z
  .object({
    competencies: SuccessPhenotypeCompetency.array(),
  })
  .describe('SuccessPhenotypeResult');

/**
 * @deprecated
 */
export type SuccessPhenotypeResult = z.infer<typeof SuccessPhenotypeResult>;

// Used for both v1 and v2
export const SuccessPhenotypeFilterInput = z
  .object({
    filters: FilterDefinitionForExtras.array(),
    metric: z.string(),
    isExperimentalRun: z.boolean().optional(),
  })
  .describe('SuccessPhenotypeFilterInput');

export type SuccessPhenotypeFilterInput = z.infer<
  typeof SuccessPhenotypeFilterInput
>;

// V2 types
export const TPercentage = z.number().min(-100).max(100);
export type TPercentage = z.infer<typeof TPercentage>;

export const NumericalTraitLevelType = z
  .enum(['greaterThan', 'lessThanOrEqual', 'twoSided'])
  .describe('NumericalTraitLevelType');

export const NumericalTraitLevelTypeOneSidedOnly = z
  .enum(['greaterThan', 'lessThanOrEqual'])
  .describe('NumericalTraitLevelTypeOneSidedOnly');

export const NumericalOneSidedTraitLevel = z
  .object({
    type: NumericalTraitLevelTypeOneSidedOnly,
    value: z.number(),
    /**
     * Higher percentage means more impactful
     */
    percentage: TPercentage,
  })
  .describe('NumericalOneSidedTraitLevel');

export type NumericalOneSidedTraitLevel = z.infer<
  typeof NumericalOneSidedTraitLevel
>;

export const NumericalTwoSidedTraitLevel = z
  .object({
    type: z.literal('twoSided'),
    fromValue: z.number(),
    toValue: z.number(),
    /**
     * Higher percentage means more impactful
     */
    percentage: TPercentage,
  })
  .describe('NumericalTwoSidedTraitLevel');

export type NumericalTwoSidedTraitLevel = z.infer<
  typeof NumericalTwoSidedTraitLevel
>;

export const NumericalTraitLevel = z
  .union([NumericalOneSidedTraitLevel, NumericalTwoSidedTraitLevel])
  .describe('NumericalTraitLevel');

export type NumericalTraitLevel = z.infer<typeof NumericalTraitLevel>;

export const NumericalTrait = z
  .object({
    type: z.literal('numeric'),
    /**
     * Used for looking up the control variable and trait definitions
     * @see ControlVariableDefinition
     */
    controlVariableColumnHeaderValue: z.string().min(1),
    levels: NumericalTraitLevel.array(),
    /**
     * The number of employees that were used to calculate the trait's percentage
     */
    sampleSize: z.number().int().min(1),
  })
  .describe('NumericalTrait');

export type NumericalTrait = z.infer<typeof NumericalTrait>;

export const CategoricalTraitLevel = z
  .object({
    type: z.literal('equal'),
    /**
     * @example For negotiation skills, this could be some enum like 'Intermediate' or 'Above average'
     */
    value: z.string().min(1),
    /**
     * Higher percentage means more impactful
     */
    percentage: TPercentage,
  })
  .describe('CategoricalTraitLevel');

export type CategoricalTraitLevel = z.infer<typeof CategoricalTraitLevel>;

export const CategoricalTrait = z
  .object({
    type: z.literal('categorical'),
    /**
     * Used for looking up the control variable and trait definitions
     * @see ControlVariableDefinition
     */
    controlVariableColumnHeaderValue: z.string().min(1),
    levels: CategoricalTraitLevel.array(),
    /**
     * The number of employees that were used to calculate the trait's percentage
     */
    sampleSize: z.number().int().min(1),
  })
  .describe('CategoricalTrait');
export type CategoricalTrait = z.infer<typeof CategoricalTrait>;

export const Trait = z
  .union([NumericalTrait, CategoricalTrait])
  .describe('Trait');
export type Trait = z.infer<typeof Trait>;

export const TraitLevel = z
  .union([NumericalTraitLevel, CategoricalTraitLevel])
  .describe('TraitLevel');
export type TraitLevel = z.infer<typeof TraitLevel>;

export const SuccessTraitsOutputFile = 'success_traits.json';
export const SuccessTraitsFinalOutputFile = 'success_traits_final.json';

export const SuccessPhenotypeV2ErrorStateReason = z
  .enum(['noFactors', 'notEnoughData'])
  .describe('SuccessPhenotypeV2ErrorStateReason');

export type SuccessPhenotypeV2ErrorStateReason = z.infer<
  typeof SuccessPhenotypeV2ErrorStateReason
>;

export const SuccessPhenotypeV2ErrorStateReasonDefault = 'notEnoughData';

export const SuccessPhenotypeV2Result = z
  .object({
    traits: Trait.array(),
    errorStateReason: SuccessPhenotypeV2ErrorStateReason.optional(),
    explanations: ExplanationsOutputDictionary.optional(),
    fullSampleSize: z.number().optional(),
    dataQualityScore: z.number().optional(),
  })
  .describe('SuccessPhenotypeV2Result');

export type SuccessPhenotypeV2Result = z.infer<typeof SuccessPhenotypeV2Result>;

export const SuccessPhenotypeBubbleGraphDatum = z.object({
  trait: Trait,
  bestTraitLevel: TraitLevel,
});

export type SuccessPhenotypeBubbleGraphDatum = z.infer<
  typeof SuccessPhenotypeBubbleGraphDatum
>;

// This is to help us get the display name of the trait based on the control variable column header value
export const ControlVariableDefinitionsMapping = z.record(
  z.string(),
  ControlVariableDefinition,
);

export type ControlVariableDefinitionsMapping = z.infer<
  typeof ControlVariableDefinitionsMapping
>;

export const KeyFactorsImpact = TPercentage;

export type KeyFactorsImpact = z.infer<typeof KeyFactorsImpact>;

export const KeyFactorsImpactDataPoint = z.object({
  impact: KeyFactorsImpact,
  keyFactorImpactValue: z.string(),
  keyFactorImpactLabel: z.string(),
  controlVariableColumnHeaderValue: z.string(),
  sampleSize: z.number(),
});

export type KeyFactorsImpactDataPoint = z.infer<
  typeof KeyFactorsImpactDataPoint
>;

export const KeyFactorsDataQualityScoreLevel = z
  .enum(['high', 'medium', 'low'])
  .describe('KeyFactorsDataQualityScoreLevel');

export type KeyFactorsDataQualityScoreLevel = z.infer<
  typeof KeyFactorsDataQualityScoreLevel
>;

export const KeyFactorsRadialImpactChartSerializableProps = z.object({
  dataPoints: KeyFactorsImpactDataPoint.array(),
  dataQualityScoreLevel: KeyFactorsDataQualityScoreLevel,
  numberOfPeople: z.number(),
  isTraitHovered: z.boolean().array(),
});

export type KeyFactorsRadialImpactChartSerializableProps = z.infer<
  typeof KeyFactorsRadialImpactChartSerializableProps
>;

export const OptimizingCardSerializableProps = z.object({
  metricColumnHeaderValue: z.string(),
  dataPoints: KeyFactorsImpactDataPoint.array(),
  traits: Trait.array(),
  errorStateReason: SuccessPhenotypeV2ErrorStateReason.optional(),
  explanations: ExplanationsOutputDictionary.optional(),
});

export type OptimizingCardSerializableProps = z.infer<
  typeof OptimizingCardSerializableProps
>;

export const BreakdownSectionSerializableProps = z.object({
  metricColumnHeaderValue: z.string(),
  controlVariableColumnHeaderValue: z.string(),
  trait: Trait,
  traits: Trait.array(),
  explanations: ExplanationsOutputDictionary.optional(),
});

export type BreakdownSectionSerializableProps = z.infer<
  typeof BreakdownSectionSerializableProps
>;

export const determineSuccessPhenotypeV2ErrorStateReason = ({
  traits,
  errorStateReason,
  isVizFailure,
}: {
  traits: Trait[];
  errorStateReason: SuccessPhenotypeV2ErrorStateReason | undefined;
  isVizFailure: boolean;
}): SuccessPhenotypeV2ErrorStateReason | undefined => {
  if (errorStateReason) {
    return errorStateReason;
  }

  if (traits.length === 0 || isVizFailure) {
    return SuccessPhenotypeV2ErrorStateReasonDefault;
  }

  return undefined;
};

export const getKeyFactorsDataQualityScoreLevel = (
  data: SuccessPhenotypeV2Result | null,
): KeyFactorsDataQualityScoreLevel => {
  const dataQualityScore =
    data?.dataQualityScore === undefined ? 0.5 : data.dataQualityScore;
  let dataQualityScoreLevel: 'high' | 'medium' | 'low' = 'medium';
  if (dataQualityScore < 0.33) {
    dataQualityScoreLevel = 'low';
  } else if (dataQualityScore > 0.66) {
    dataQualityScoreLevel = 'high';
  } else {
    dataQualityScoreLevel = 'medium';
  }

  return dataQualityScoreLevel;
};
